import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  X,
  Users,
  FolderKanban,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import { useDebounce } from '../hooks/useDebounce';
import { BRAND } from '../config/brand';
import { Button } from './ui';

export default function SearchModal({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const { data: results, isLoading, error } = useGlobalSearch(debouncedQuery);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Clear query when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSelect = (type, id) => {
    navigate(type === 'applicant' ? `/applicants/${id}` : `/cases/${id}`);
    onClose();
  };

  if (!isOpen) return null;

  const hasResults = results && (results.applicants?.length > 0 || results.cases?.length > 0);
  const showNoResults = debouncedQuery.length >= 2 && !isLoading && !hasResults;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed top-[20%] left-1/2 -translate-x-1/2 w-[90%] max-w-[560px] bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-700 rounded-2xl shadow-2xl z-[1001] overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-grey-200 dark:border-grey-700">
          <Search size={20} className="text-grey-400" />
          <input
            ref={inputRef}
            type="text"
            className="flex-1 bg-transparent border-none outline-none text-base text-grey-900 dark:text-grey-100 placeholder:text-grey-400"
            placeholder="Search applicants, cases..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X size={16} />
          </Button>
        </div>

        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center gap-3 py-10 text-grey-500">
              <Loader2 size={20} className="animate-spin" />
              Searching...
            </div>
          )}

          {error && (
            <div className="py-10 text-center text-grey-500">
              <Search size={32} className="mx-auto mb-3 opacity-50" />
              <h4 className="font-medium text-grey-900 dark:text-grey-100 mb-1">Search Error</h4>
              <p>Unable to search. Please try again.</p>
            </div>
          )}

          {showNoResults && (
            <div className="py-10 text-center text-grey-500">
              <Search size={32} className="mx-auto mb-3 opacity-50" />
              <h4 className="font-medium text-grey-900 dark:text-grey-100 mb-1">No results found</h4>
              <p>Try a different search term</p>
            </div>
          )}

          {hasResults && (
            <>
              {results.applicants?.length > 0 && (
                <div className="px-5 py-3">
                  <div className="flex items-center gap-2 text-[11px] font-semibold text-grey-500 uppercase tracking-wider mb-2">
                    <Users size={14} />
                    Applicants ({results.applicants.length})
                  </div>
                  {results.applicants.map((applicant) => (
                    <div
                      key={applicant.id}
                      className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-grey-100 dark:hover:bg-grey-800 transition-colors group"
                      onClick={() => handleSelect('applicant', applicant.id)}
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-teal-500/15 text-teal-600 dark:text-teal-400">
                        <Users size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {applicant.first_name && applicant.last_name
                            ? `${applicant.first_name} ${applicant.last_name}`
                            : applicant.email?.split('@')[0] || `Applicant ${applicant.id.slice(0, 8)}`}
                        </div>
                        <div className="text-xs text-grey-500 truncate">
                          {applicant.email} • {applicant.status || 'pending'}
                        </div>
                      </div>
                      <ArrowRight size={16} className="text-grey-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ))}
                </div>
              )}

              {results.cases?.length > 0 && (
                <div className="px-5 py-3">
                  <div className="flex items-center gap-2 text-[11px] font-semibold text-grey-500 uppercase tracking-wider mb-2">
                    <FolderKanban size={14} />
                    Cases ({results.cases.length})
                  </div>
                  {results.cases.map((caseItem) => (
                    <div
                      key={caseItem.id}
                      className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-grey-100 dark:hover:bg-grey-800 transition-colors group"
                      onClick={() => handleSelect('case', caseItem.id)}
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-500/15 text-amber-600 dark:text-amber-400">
                        <FolderKanban size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{caseItem.title}</div>
                        <div className="text-xs text-grey-500 truncate">
                          {caseItem.status} • {caseItem.priority || 'normal'} priority
                        </div>
                      </div>
                      <ArrowRight size={16} className="text-grey-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {!isLoading && query.length < 2 && (
            <div className="py-10 text-center text-grey-500">
              <Search size={32} className="mx-auto mb-3 opacity-50" />
              <h4 className="font-medium text-grey-900 dark:text-grey-100 mb-1">Search {BRAND.name}</h4>
              <p>Type at least 2 characters to search</p>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-5 py-3 text-xs text-grey-500 text-center border-t border-grey-200 dark:border-grey-700">
          Press <kbd className="bg-grey-100 dark:bg-grey-800 px-1.5 py-0.5 rounded font-mono text-[11px]">Esc</kbd> to close • <kbd className="bg-grey-100 dark:bg-grey-800 px-1.5 py-0.5 rounded font-mono text-[11px]">Enter</kbd> to select
        </div>
      </div>
    </>
  );
}
